import { useState, useEffect } from "react";
import axios from "axios";
import useDebouncedValue from "../../libs/useDebouncedValue";
import { filterMarketplaceProducts } from "../../libs/marketplaceProductVisibility";

const EMPTY_RESULTS = { products: [], query: "", count: 0, page: 1, totalPages: 0 };

const useSearchProducts = (query, options = {}) => {
  const {
    limit = 24,
    page = 1,
    sort = "name",
    category,
    minPrice,
    maxPrice,
    minLength = 2,
    enabled = true,
    retryKey = 0,
  } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState(EMPTY_RESULTS);
  const debouncedQuery = useDebouncedValue(query, 300);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();

    if (!enabled || trimmed.length < minLength) {
      setSearchResults(EMPTY_RESULTS);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSearchResults = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/product/search`,
          {
            params: {
              q: trimmed,
              letter: trimmed,
              limit,
              page,
              sort,
              category,
              minPrice,
              maxPrice,
            },
          }
        );
        if (!cancelled) {
          const products = filterMarketplaceProducts(response.data?.products || []);
          setSearchResults({
            products,
            query: response.data?.query || trimmed,
            count: response.data?.count ?? products.length,
            page: response.data?.page ?? page,
            totalPages: response.data?.totalPages ?? 0,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              "Could not search products. Check your connection and try again."
          );
          setSearchResults(EMPTY_RESULTS);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchSearchResults();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, enabled, limit, page, sort, category, minPrice, maxPrice, minLength, retryKey]);

  return { isLoading, error, searchResults };
};

export default useSearchProducts;
